import { Icon } from '@components/Icon';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { win } from '@utils/win';
import { CSSProperties, FC, forwardRef, useState } from 'react';
import { useMountedState, useWindowSize } from 'react-use';
import { Button, ButtonProps } from '../Button';
import { TextArea, Props as TextAreaProps } from '../TextArea';
import { Tooltip } from '../Tooltip';
import { VisuallyHidden } from '../VisuallyHidden';

interface BaseProps {
  value: string;
}

interface CopyButtonExtraProps {
  /**
   * Inline styles forwarded to the tooltip's floating element. Useful for
   * raising the tooltip's z-index above high stacking contexts (e.g. toasts).
   */
  tooltipStyle?: CSSProperties;
}

type CopyButtonProps = ButtonProps & BaseProps & CopyButtonExtraProps;

const COPY_BUTTON_WIDTH = 28;
const COPY_BUTTON_HEIGHT = 28;
const LABEL_TRANSITION_MS = 2000;

// ts-unused-exports:disable-next-line
export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ value, ...originalProps }, ref) => {
    // Evaluate translations lazily inside the component so they run after the
    // locale is activated (module-level `t` calls throw before i18n.activate).
    const DEFAULT_LABEL = t`Copy to clipboard`;
    const COPIED_LABEL = t`Copied!`;
    const { width } = useWindowSize();
    const { className, tooltipStyle, ...buttonProps } = originalProps;
    const [transitionTimer, setTransitionTimer] = useState(0);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const isMounted = useMountedState();
    return (
      <div
        css={{
          display: 'inline-block',
          position: 'relative',
        }}
        className={className}
      >
        <Tooltip
          // TODO: this isn't great to remount the component like this, but @reach/tooltip conflicts with our goals of continuing to show the tooltip when the button is clicked. If we try to use it normally without the position:absolute div hack, you can resize normally, but clicking will make the tooltip disappear.
          key={width}
          label={transitionTimer ? COPIED_LABEL : DEFAULT_LABEL}
          isVisible={tooltipVisible || undefined}
          style={tooltipStyle}
        >
          <div
            css={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: 0,
              top: 0,
              pointerEvents: 'none',
            }}
          />
        </Tooltip>
        <Button
          {...buttonProps}
          css={{
            width: COPY_BUTTON_WIDTH,
            height: COPY_BUTTON_HEIGHT,
            padding: 0,
          }}
          ref={ref}
          onMouseEnter={(): void => {
            setTooltipVisible(true);
          }}
          onMouseLeave={(): void => {
            clearTimeout(transitionTimer);
            setTransitionTimer(0);
            return setTooltipVisible(false);
          }}
          onClick={(): void => {
            win.navigator.clipboard.writeText(value);
            clearTimeout(transitionTimer);
            const timeoutNum = win.setTimeout(() => {
              if (isMounted()) {
                setTransitionTimer(0);
              }
            }, LABEL_TRANSITION_MS);
            setTransitionTimer(timeoutNum);
          }}
          name="copy"
        >
          <Icon i="copy" size="sm" color="primary" />
          <VisuallyHidden>
            <Trans>Copy to Clipboard</Trans>
          </VisuallyHidden>
        </Button>
      </div>
    );
  },
);

type CopyTextProps = TextAreaProps & BaseProps;

export const CopyText: FC<CopyTextProps> = (rawProps) => {
  const { value, ...props } = rawProps;
  const [stateData, setStateData] = useState(value || '');
  return (
    <div css={{ position: 'relative' }}>
      <TextArea
        {...props}
        css={{ paddingRight: COPY_BUTTON_WIDTH + 5 }}
        onChange={(evt): void => {
          props.onChange?.(evt);
          setStateData((evt.target as fixMe).value);
        }}
        value={stateData}
      />
      <CopyButton
        name="copy"
        value={stateData}
        css={{ position: 'absolute', bottom: 2, right: 2 }}
      />
    </div>
  );
};
